import {
  ApprovalStatus,
  formatCurrency,
  UserRole,
} from "@employee-management/common";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  SearchFilterBar,
  type FilterSettings,
  type FilterOption,
} from "@/components/ui/search-filter-bar";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Calendar,
  Clock,
  DollarSign,
  Activity,
  User,
  Edit,
  Dot,
} from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import type { OvertimeEntry } from "@/types/project.types";
import { apiService } from "@/lib/api-service";
import { useAuth } from "@/lib/auth-context";
import { filterData } from "@/lib/filter-utils";
import { OvertimeApprovalDialog } from "../../../../overtime/PendingApprovalsView/components/PendingApprovalsTable/OvertimeApprovalDialog";
import { OvertimeEditDialog } from "./OvertimeEditDialog";
import { OvertimeResendDialog } from "./OvertimeResendDialog";
import { OvertimeDetailsDialog } from "./OvertimeDetailsDialog";
import { PaymentStatusToggle } from "./PaymentStatusToggle";
import { StatusDropdown } from "./StatusDropdown";
import { useSearch } from "@tanstack/react-router";
import clsx from "clsx";

interface OvertimeTableProps {
  projectId: string;
  isActive: boolean;
  projectMembers: Array<{
    id: string;
    name: string;
    email: string;
    department: string;
    hourlyRate: number;
  }>;
}

type SortField =
  | "date"
  | "totalHours"
  | "totalAmount"
  | "approvalStatus"
  | "createdBy";
type SortDirection = "asc" | "desc";

const ApprovalStatusSortOrder: Record<ApprovalStatus, number> = {
  [ApprovalStatus.PENDING]: 0,
  [ApprovalStatus.REJECTED]: 1,
  [ApprovalStatus.APPROVED]: 2,
};

interface SortState {
  field: SortField;
  direction: SortDirection;
}

export function OvertimeTable({
  projectId,
  isActive,
  projectMembers,
}: OvertimeTableProps) {
  const { entryId: overtimeEntryId } = useSearch({
    from: "/projects/$projectId",
  });
  const { hasRole, user } = useAuth();
  const [selectedEntry, setSelectedEntry] = useState<OvertimeEntry | null>(
    null
  );
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<OvertimeEntry | null>(null);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [sorting, setSorting] = useState<SortState>({
    field: "approvalStatus",
    direction: "desc",
  });
  const [filterSettings, setFilterSettings] = useState<FilterSettings>({
    searchTerm: "",
    filters: {},
  });

  const [selectedOvertimeEntryId, setSelectedOvertimeEntryId] = useState<
    string | null
  >(null);

  // Determine if user can see financial information
  const canSeeFinancialInfo = hasRole(UserRole.MANAGER);

  // Filter options for the search filter bar
  const filterOptions: FilterOption[] = useMemo(() => {
    const options: FilterOption[] = [
      {
        key: "date",
        label: "Date",
        type: "range",
        rangeType: "date",
      },
      {
        key: "totalHours",
        label: "Total Hours",
        type: "range",
        rangeType: "number",
      },
      {
        key: "approvalStatus",
        label: "Status",
        type: "select",
        options: [
          { value: ApprovalStatus.PENDING, label: "Pending" },
          { value: ApprovalStatus.APPROVED, label: "Approved" },
          { value: ApprovalStatus.REJECTED, label: "Rejected" },
        ],
      },
      ...(canSeeFinancialInfo
        ? [
            {
              key: "paymentStatus",
              label: "Payment Status",
              type: "select" as const,
              options: [
                { value: "true", label: "Paid" },
                { value: "false", label: "Unpaid" },
              ],
            },
          ]
        : []),
      {
        key: "createdBy",
        label: "Created By",
        type: "select",
        options: [{ value: "Admin User", label: "Admin User" }],
      },
    ];

    // Add financial filters only for managers
    if (canSeeFinancialInfo) {
      options.splice(2, 0, {
        key: "totalAmount",
        label: "Total Amount",
        type: "range",
        rangeType: "number",
      });
    }

    return options;
  }, [canSeeFinancialInfo]);

  const {
    data: overtimeEntries,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["overtime", "project", projectId],
    queryFn: () =>
      apiService.get<OvertimeEntry[]>(`/overtime/project/${projectId}`),
  });

  const selectedOvertimeEntry = useMemo(() => {
    const myEntry = overtimeEntries?.find(
      (entry) => entry.id === selectedOvertimeEntryId
    );
    if (!myEntry) return null;

    return {
      ...myEntry,
      project: {
        ...myEntry.project,
        id: projectId,
        name: "",
        clientName: "",
      },
    };
  }, [overtimeEntries, selectedOvertimeEntryId]);

  useEffect(() => {
    console.log(overtimeEntries);

    if (overtimeEntries && overtimeEntries.length > 0) {
      setTimeout(() => {
        const overtimeEntry = document.querySelector(
          `[data-ot-selected="true"]`
        );
        if (overtimeEntry) {
          overtimeEntry.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    }
  }, [overtimeEntries]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const calculateTotalHours = (employees: OvertimeEntry["employees"]) => {
    return employees.reduce((total, emp) => total + emp.hours, 0);
  };

  const calculateTotalAmount = (employees: OvertimeEntry["employees"]) => {
    return employees.reduce((total, emp) => total + (emp.total || 0), 0);
  };

  const startActionProcess = (e: React.MouseEvent, entry: OvertimeEntry) => {
    e.preventDefault();
    e.stopPropagation();

    setSelectedEntry({
      ...entry,
      project: {
        id: "",
        name: "",
        clientName: "",
      },
    });
    setApprovalDialogOpen(true);
  };

  const handleApprovalComplete = () => {
    setApprovalDialogOpen(false);
    setSelectedEntry(null);
    refetch();
  };

  const startEditProcess = (e: React.MouseEvent, entry: OvertimeEntry) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isActive) {
      return;
    }

    setEditEntry({
      ...entry,
      project: {
        id: projectId,
        name: "",
        clientName: "",
      },
    });
    setEditDialogOpen(true);
  };

  const handleEditComplete = () => {
    setEditDialogOpen(false);
    setEditEntry(null);
    refetch();
  };

  const startResendProcess = (e: React.MouseEvent, entry: OvertimeEntry) => {
    e.preventDefault();
    e.stopPropagation();

    setSelectedEntry(entry);
    setResendDialogOpen(true);
  };

  const handleResendComplete = () => {
    setResendDialogOpen(false);
    setSelectedEntry(null);
    refetch();
  };

  const openDetailsDialog = (entry: OvertimeEntry) => {
    setSelectedOvertimeEntryId(entry.id);
    setDetailsDialogOpen(true);
  };

  const handleSort = (field: SortField) => {
    setSorting((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (field: SortField) => {
    if (sorting.field !== field) {
      return <ArrowUpDown className="h-4 w-4" />;
    }
    return sorting.direction === "asc" ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  // Process and filter the data
  const processedEntries = useMemo(() => {
    if (!overtimeEntries) return [];

    // Add calculated fields for filtering
    const entriesWithCalculatedFields = overtimeEntries.map((entry) => ({
      ...entry,
      totalHours: calculateTotalHours(entry.employees),
      totalAmount: calculateTotalAmount(entry.employees),
      createdBy: "Admin User", // Using the hardcoded value for now
    }));

    // Apply filters
    const filteredEntries = filterData(
      entriesWithCalculatedFields,
      filterSettings,
      ["createdBy", "notes"] // Search fields
    );

    // Sort the filtered data
    return filteredEntries.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sorting.field) {
        case "date":
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case "totalHours":
          aValue = a.totalHours;
          bValue = b.totalHours;
          break;
        case "totalAmount":
          aValue = a.totalAmount;
          bValue = b.totalAmount;
          break;
        case "approvalStatus":
          aValue = ApprovalStatusSortOrder[a.approvalStatus as ApprovalStatus];
          bValue = ApprovalStatusSortOrder[b.approvalStatus as ApprovalStatus];
          break;
        case "createdBy":
          aValue = a.createdBy;
          bValue = b.createdBy;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sorting.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sorting.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [overtimeEntries, filterSettings, sorting]);

  const calculatedValues = useMemo(() => {
    return {
      totalPendingHours: processedEntries.reduce(
        (total, entry) =>
          total +
          entry.totalHours *
            (entry.approvalStatus === ApprovalStatus.PENDING ? 1 : 0),
        0
      ),
      totalPendingAmount: processedEntries.reduce(
        (total, entry) =>
          total +
          entry.totalAmount *
            (entry.approvalStatus === ApprovalStatus.PENDING ? 1 : 0),
        0
      ),
      totalApprovedHours: processedEntries.reduce(
        (total, entry) =>
          total +
          entry.totalHours *
            (entry.approvalStatus === ApprovalStatus.APPROVED ? 1 : 0),
        0
      ),
      totalApprovedAmount: processedEntries.reduce(
        (total, entry) =>
          total +
          entry.totalAmount *
            (entry.approvalStatus === ApprovalStatus.APPROVED ? 1 : 0),
        0
      ),
      totalHours: processedEntries.reduce(
        (total, entry) => total + entry.totalHours,
        0
      ),
      totalAmount: processedEntries.reduce(
        (total, entry) => total + entry.totalAmount,
        0
      ),
    };
  }, [processedEntries]);

  // Early returns after all hooks
  if (isLoading) return <div>Loading overtime entries...</div>;
  if (isError)
    return <div className="text-red-500">Failed to load overtime entries.</div>;

  return (
    <div className="space-y-4">
      <SearchFilterBar
        filterOptions={filterOptions}
        placeholder="Search overtime entries by creator name or notes..."
        onFilterChange={setFilterSettings}
      />

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="!bg-muted/40">
              <TableHead className="w-[3ch] !p-0">&nbsp;</TableHead>
              <TableHead>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleSort("date")}
                  className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
                >
                  <Calendar className="h-4 w-4" />
                  Date
                  {getSortIcon("date")}
                </Button>
              </TableHead>

              <TableHead>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleSort("approvalStatus")}
                  className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
                >
                  <Activity className="h-4 w-4" />
                  Status
                  {getSortIcon("approvalStatus")}
                </Button>
              </TableHead>

              {canSeeFinancialInfo && (
                <TableHead className="text-center">Payment</TableHead>
              )}
              <TableHead>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleSort("createdBy")}
                  className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent"
                >
                  <User className="h-4 w-4" />
                  Created By
                  {getSortIcon("createdBy")}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleSort("totalHours")}
                  className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent ml-auto"
                >
                  <Clock className="h-4 w-4" />
                  Total Hours
                  {getSortIcon("totalHours")}
                </Button>
              </TableHead>
              {canSeeFinancialInfo && (
                <TableHead>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleSort("totalAmount")}
                    className="flex items-center gap-2 p-0 h-auto font-medium hover:bg-transparent ml-auto"
                  >
                    <DollarSign className="h-4 w-4" />
                    Total Amount
                    {getSortIcon("totalAmount")}
                  </Button>
                </TableHead>
              )}
              <TableHead className="text-right">&nbsp;</TableHead>
            </TableRow>
          </TableHeader>

          {processedEntries?.length === 0 ? (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={canSeeFinancialInfo ? 9 : 7}
                  className="text-center py-8 text-muted-foreground"
                >
                  No overtime entries found.
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {processedEntries?.map((entry) => (
                <TableRow
                  key={entry.id}
                  data-ot-selected={entry.id === overtimeEntryId}
                  onClick={() =>
                    openDetailsDialog({
                      ...entry,
                      project: {
                        id: projectId,
                        name: "",
                        clientName: "",
                      },
                    })
                  }
                  className={clsx("cursor-pointer")}
                >
                  <TableCell className="w-[3ch] !p-0 !text-center">
                    {overtimeEntryId === entry.id && (
                      <Dot className="mx-auto w-12 h-12 text-destructive animate-ping" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatDate(entry.date)}
                  </TableCell>

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <StatusDropdown
                      overtimeEntryId={entry.id}
                      currentStatus={entry.approvalStatus as ApprovalStatus}
                      projectId={projectId}
                      disabled={!isActive}
                    />
                  </TableCell>

                  {canSeeFinancialInfo && (
                    <TableCell className="text-center">
                      <PaymentStatusToggle
                        overtimeEntryId={entry.id}
                        paymentStatus={entry.paymentStatus}
                        partialPaymentStatus={Boolean(
                          entry.employees.find(
                            (employee) => employee.paymentStatus
                          )
                        )}
                        approvalStatus={entry.approvalStatus}
                        projectId={projectId}
                      />
                    </TableCell>
                  )}

                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">
                        {entry.createdByRef?.name || "Unknown"}
                      </div>
                      <div className="text-muted-foreground">
                        {entry.createdByRef?.email || "No email"}
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="text-right">
                    {entry.totalHours.toFixed(1)}h
                  </TableCell>
                  {canSeeFinancialInfo && (
                    <TableCell className="text-right">
                      {formatCurrency(entry.totalAmount)}
                    </TableCell>
                  )}

                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-end space-x-2">
                      {/* Edit button for non-supervisor users */}
                      {hasRole(UserRole.MANAGER) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => startEditProcess(e, entry)}
                          className="text-green-600 hover:text-green-700"
                          disabled={!isActive}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      )}
                      {entry.approvalStatus === ApprovalStatus.PENDING &&
                        hasRole(UserRole.MANAGER) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => startActionProcess(e, entry)}
                            className="text-primary/80 hover:text-primary"
                            disabled={!isActive}
                          >
                            Take Action
                          </Button>
                        )}
                      {entry.approvalStatus === ApprovalStatus.REJECTED &&
                        user?.role === UserRole.SUPERVISOR && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => startResendProcess(e, entry)}
                            className="text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
                            disabled={!isActive}
                          >
                            Resend
                          </Button>
                        )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}

              {processedEntries.length > 0 && (
                <>
                  {/* Pending */}
                  <TableRow className="hover:!bg-transparent">
                    <TableCell
                      colSpan={canSeeFinancialInfo ? 4 : 3}
                      className="text-right text-muted-foreground"
                    >
                      Pending
                    </TableCell>
                    <TableCell
                      colSpan={1}
                      className="text-right text-muted-foreground"
                    >
                      {calculatedValues.totalPendingHours.toFixed(1)}h
                    </TableCell>
                    {canSeeFinancialInfo && (
                      <TableCell
                        colSpan={1}
                        className="text-right text-muted-foreground"
                      >
                        {formatCurrency(calculatedValues.totalPendingAmount)}
                      </TableCell>
                    )}
                  </TableRow>

                  {/* Approved */}
                  <TableRow className="hover:!bg-transparent">
                    <TableCell
                      colSpan={canSeeFinancialInfo ? 4 : 3}
                      className="text-right text-muted-foreground"
                    >
                      Approved
                    </TableCell>
                    <TableCell
                      colSpan={1}
                      className="text-right text-muted-foreground"
                    >
                      {calculatedValues.totalApprovedHours.toFixed(1)}h
                    </TableCell>
                    {canSeeFinancialInfo && (
                      <TableCell
                        colSpan={1}
                        className="text-right text-muted-foreground"
                      >
                        {formatCurrency(calculatedValues.totalApprovedAmount)}
                      </TableCell>
                    )}
                  </TableRow>

                  <TableRow className="hover:!bg-transparent">
                    <TableCell
                      colSpan={canSeeFinancialInfo ? 4 : 3}
                      className="text-right"
                    >
                      Total
                    </TableCell>
                    <TableCell colSpan={1} className="text-right">
                      {calculatedValues.totalHours.toFixed(1)}h
                    </TableCell>
                    {canSeeFinancialInfo && (
                      <TableCell colSpan={1} className="text-right">
                        {formatCurrency(calculatedValues.totalAmount)}
                      </TableCell>
                    )}
                  </TableRow>
                </>
              )}
            </TableBody>
          )}
        </Table>
      </div>

      {selectedEntry && (
        <OvertimeApprovalDialog
          open={approvalDialogOpen}
          onOpenChange={setApprovalDialogOpen}
          overtimeEntry={selectedEntry}
          onComplete={handleApprovalComplete}
          isProjectView={true}
        />
      )}

      {selectedEntry && (
        <OvertimeResendDialog
          open={resendDialogOpen}
          onOpenChange={setResendDialogOpen}
          overtimeEntry={{
            ...selectedEntry,
            project: {
              id: "",
              name: "",
              clientName: "",
            },
          }}
          onComplete={handleResendComplete}
        />
      )}

      <OvertimeDetailsDialog
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        overtimeEntry={selectedOvertimeEntry || null}
      />

      {editEntry && (
        <OvertimeEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          overtimeEntry={editEntry}
          projectMembers={projectMembers}
          onComplete={handleEditComplete}
        />
      )}
    </div>
  );
}
